import { MultiValue, Select } from "chakra-react-select";
import { useEffect, useMemo } from "react";
import {
  menuPortalStyles,
  dropdownIndicatorStyle,
  indicatorSeparatorStyle,
  baseOptionStyle,
  placeholderStyle,
  valueContainerStyle,
} from "./styles";

export type MultiSelectOption = {
  value: string | number;
  label: string;
};

export type MultiSelectProps = {
  options?: MultiSelectOption[];
  value?: MultiSelectOption[];
  onChange?: (newValue: MultiSelectOption[]) => void;
  onAllSelectedChange?: (allSelected: boolean) => void;
  placeholder?: string;
  isDisabled?: boolean;
  allLabel?: string;
};

const ALL_VALUE = "__all__";

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  onAllSelectedChange,
  placeholder = "Auswählen...",
  isDisabled,
  allLabel = "Alle",
}: MultiSelectProps) => {
  const allSelected = value.length === options.length && options.length > 0;

  // Notify parent of allSelected state (including initial)
  useEffect(() => {
    onAllSelectedChange?.(allSelected);
  }, [allSelected, onAllSelectedChange]);

  const allOption: MultiSelectOption = useMemo(
    () => ({ value: ALL_VALUE, label: allLabel }),
    [allLabel]
  );

  const optionsWithSelectAll = useMemo(() => {
    if (options.length === 0 || allSelected) return options;
    return [allOption, ...options];
  }, [options, allSelected, allOption]);

  // Display "Alle" badge when all selected (unless only 1 option)
  const displayValue =
    allSelected && options.length > 1 ? [allOption] : value;

  const handleChange = (newValue: MultiValue<MultiSelectOption>) => {
    if (!onChange) return;

    const realNewValue = newValue.filter((opt) => opt.value !== ALL_VALUE);
    const hasAllValue = newValue.some((opt) => opt.value === ALL_VALUE);

    // Not all selected, user clicked "Select All"
    if (!allSelected && hasAllValue) {
      onChange([...options]);
      return;
    }

    // All selected, user removed the "Alle" badge
    if (allSelected && !hasAllValue) {
      onChange([]);
      return;
    }

    // All selected, user clicked an option to deselect
    if (allSelected && realNewValue.length > 0) {
      const toggledOption = realNewValue[0];
      const newSelection = options.filter(
        (opt) => opt.value !== toggledOption.value
      );
      onChange(newSelection);
      return;
    }

    onChange([...realNewValue]);
  };

  // When all selected, show checkmarks on all options in dropdown
  const isOptionSelected = (option: MultiSelectOption) => {
    if (allSelected) {
      return options.some((opt) => opt.value === option.value);
    }
    return value.some((opt) => opt.value === option.value);
  };

  return (
    <Select
      isMulti
      value={displayValue}
      options={optionsWithSelectAll}
      onChange={handleChange}
      isDisabled={isDisabled}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      placeholder={placeholder}
      noOptionsMessage={() => "Keine Optionen"}
      isOptionSelected={isOptionSelected}
      menuPortalTarget={document.body}
      styles={menuPortalStyles}
      chakraStyles={{
        control: (provided) => ({
          ...provided,
          minWidth: "200px",
          bg: "white",
          cursor: "pointer",
          _focus: {
            borderColor: "gray.300",
            boxShadow: "none",
          },
        }),
        dropdownIndicator: dropdownIndicatorStyle,
        indicatorSeparator: indicatorSeparatorStyle,
        option: (provided, state) => ({
          ...baseOptionStyle(provided, state.isSelected),
          fontWeight: state.data.value === ALL_VALUE ? "semibold" : "normal",
        }),
        placeholder: placeholderStyle,
        valueContainer: valueContainerStyle,
        multiValue: (provided) => ({
          ...provided,
          bg: "gray.700",
          borderRadius: "full",
        }),
        multiValueLabel: (provided) => ({
          ...provided,
          color: "white",
          fontSize: "sm",
        }),
        multiValueRemove: (provided) => ({
          ...provided,
          color: "white",
          opacity: 0.7,
          _hover: {
            bg: "transparent",
            color: "white",
            opacity: 1,
          },
        }),
      }}
    />
  );
};

export default MultiSelect;
